import { useCallback } from 'react';

import { useTeams } from './useTeams';

export type UseTeamKitImagesResult = {
  /**
   * The kit URL for a team. Undefined while teams load and for a code the API
   * does not recognise — the caller falls back to its bundled shirt in both
   * cases.
   */
  resolveTeamKit: (
    teamShortName?: string,
    isGoalkeeper?: boolean
  ) => string | undefined;
};

/**
 * For the surfaces that render a shirt with no player in scope, and so have
 * only a team short name to go on.
 *
 * This fetches, via `useTeams` — which caches for an hour — so keep it to
 * components that genuinely have no player. Anything holding a player already
 * has `kitImageUrl` on it and should use that directly rather than looking the
 * team up again.
 */
export const useTeamKitImages = (): UseTeamKitImagesResult => {
  const { teams } = useTeams();

  const resolveTeamKit = useCallback(
    (teamShortName?: string, isGoalkeeper = false) => {
      if (!teamShortName) return undefined;

      const team = teams.find(
        (candidate) => candidate.shortName === teamShortName
      );
      if (!team) return undefined;

      return isGoalkeeper ? team.goalkeeperKitImageUrl : team.kitImageUrl;
    },
    [teams]
  );

  return { resolveTeamKit };
};
